import React, { useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { i18nIntl, getRoundLabel, isNotEmpty, isColorBright, RGB_2_HEX } from '../../lib/ep.js';
import { useCSS } from '../../lib/hooks.js';

import RoundSpinner from '../builder/RoundSpinner';

import BracketsCanvas from '../games/BracketsCanvas';

import AnimatedTick from '../svg/AnimatedTick';
import LeveledHTMLText from './helpers/LeveledHTMLText.js';

const Wrapper = (props) => {
	const is_preview = props?.is_preview ?? false;
	if(!is_preview) return props.children;

	return (
		<div class="main_contents_flex" style={{width:'100%'}}>
			{props.children}
		</div>
	)
}

const BracketQuestion = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ is_hovering, setIsHovering ] = useState(false);
	const [ hover_item, setHoverItem ] = useState(false);

	useCSS('comp/brackets.css');

	const question = props?.question?.Question ?? false;
	if(question === false) return null;

	const answers = props?.answers ?? [];
	const bracket = props?.bracket ?? {};
	const stage = props?.stage ?? {};
	const show_bracket = stage?.show_bracket ?? false;
	const hide_candidate_info = stage?.hide_candidate_info ?? false;

	const is_preview = props?.is_preview ?? false;
	const is_desktop = ((window?.platform ?? 'desktop') == 'desktop') ? true : false;

	const main_color = props?.layout_props?.color?.main_color ?? 'rgba(30, 34, 170, 1)';
	const text_color = props?.layout_props?.color?.text_color ?? 'rgba(0, 0, 0, 1)';
	const is_bright_color = isColorBright(RGB_2_HEX(main_color));
	const is_loading = props?.loading ?? false;

	const selectAnswer = (answer) => {
		if(props.answer_click != undefined) props.answer_click(answer);
	};

	const rolloverTeam = (i) => {
		if(!is_desktop) return;
		
		setIsHovering(true);
		setHoverItem(i);
	};

	const rolloutTeam = () => {
		if(!is_desktop) return;
		
		setIsHovering(false);
		setHoverItem(false);
	};

	const title = getRoundLabel(bracket, stage.round, false, props.intl);
	const subtitle = stage?.matchup_message ?? i18n("Pick your favorite");

	return (
		<Wrapper is_preview={is_preview}>
			<article class={'bracket_question_container ' + ((is_bright_color) ? 'is_light' : 'is_dark')}>
				<div class="current_match">
					{(show_bracket) && (
						<div class="gui_bracket" style={{ backgroundColor:main_color }}>
							<BracketsCanvas
								current_round={question.config.round}
								current_match={question.config.match}
								version="iconic"
								main_color={main_color}
								teams={props.teams}
								bracket={bracket}
							/>
						</div>
					)}

					<LeveledHTMLText
						_class={{ other:'texts gui_main_text_color_on_bg' }}
					>
						<h1 class="title">{title}</h1>

						{(subtitle != undefined && subtitle != "") && (
							<p class="subtitle">{subtitle}</p>
						)}
					</LeveledHTMLText>

					<div class="gui_pos_rel" style={{minHeight:'120px'}}>
						<div style={(is_loading) ? { opacity:0 } : null}>
							<div class={'teams_in_brackets' + ((is_hovering) ? ' is_hovering' : '')}>
								<div class="is_vs original_color_item" style={{ order:'1', color:text_color }}>{i18n("vs", 'uppercase')}</div>

								{answers.map((answer, i) => {
									const candidate = (props.teams).find((candidate) => (candidate.id == answer.config.bracket_candidate));
									const has_image_answer = (isNotEmpty(candidate, 'full_image')) ? true : false;
									const hide_candidate_info_hidden = (has_image_answer && hide_candidate_info) ? true : false;
									const is_flipped = (i > 0 && isNotEmpty(bracket, 'autoflip_candidates', ['0'])) ? true : false;

									const team_id = `team_${i}`;
									const tick_direction = 2 * i - 1;	// -1 / +1

									let team_bg_color = main_color;
									let team_is_bright_color = is_bright_color;
									if(!has_image_answer && isNotEmpty(candidate, 'color')) {
										team_bg_color = candidate.color;
										team_is_bright_color = isColorBright(RGB_2_HEX(team_bg_color));
									}

									let candidate_classes = ['is_team', 'gui_is_animated'];
									candidate_classes.push((team_is_bright_color) ? 'is_light' : 'is_dark');
									if(!has_image_answer) candidate_classes.push('is_only_text');

									return (
										<div
											key={`candidate_${question.id}_${i}`}
											id={team_id}
											class={candidate_classes.join(" ")}
											onClick={() => selectAnswer(answer)}
											onMouseEnter={() => rolloverTeam(i)}
											onMouseLeave={() => rolloutTeam()}
											style={{ order:(i * 2), background:team_bg_color }}
										>
											{(has_image_answer) && (
												<div class={'team_img gui_is_animated' + ((is_flipped) ? ' is_flipped' : '')} style={{ backgroundImage:`url(${candidate.full_image})` }}>
													<img src={`${window.cdn_url}img/blank_1x1.png`} width="100%" alt={candidate.name} style={{ maxWidth:'100%' }} />

													{(is_hovering && hover_item == i) && (
														<AnimatedTick
															direction={tick_direction}
															parent={team_id}
														/>
													)}
												</div>
											)}

											{(!hide_candidate_info_hidden) && (
												<p class="team_name hb_secondaryFont">{candidate.name}</p>
											)}

											{(!hide_candidate_info_hidden && isNotEmpty(candidate, 'description')) && (
												<div class="team_description" dangerouslySetInnerHTML={{ __html:candidate.description.replace(/\n/g, '<br />') }} />
											)}

											{(!has_image_answer && is_hovering && hover_item == i) && (
												<AnimatedTick
													direction={tick_direction}
													parent={team_id}
												/>
											)}
										</div>
									)
								})}
							</div>
						</div>

						{(is_loading) && (
							<LeveledHTMLText
								_class={{ other:'loading_question gui_main_text_color_on_bg is_fullscreen relative_to_container' }}
								style={{zIndex:'2'}}
							>
								<RoundSpinner fullscreen={false} data={{color:text_color}}>
									<p class="mod_align_center"><FormattedMessage id="Loading matchup #{matchup} in {round}" values={{matchup:question.config.match, round:title}} />...</p>
								</RoundSpinner>
							</LeveledHTMLText>
						)}
					</div>
				</div>
			</article>
		</Wrapper>
	)
}

export default injectIntl(BracketQuestion);